import { getCurrentUser } from "../../lib/authSession";
import api from "../../utils/api";

/*
 * Resolved-review state is authoritative on the server (per company) so it
 * follows the user across devices. localStorage is kept only as a fast cache
 * for the synchronous read helpers used across the reviews pages; call
 * syncResolvedReviewIds() on mount to reconcile the cache with the server.
 */

const KEY = (companyId) =>
  companyId ? `repman-resolved-reviews-v1:c-${companyId}` : null;

function readSet(companyId) {
  const key = KEY(companyId);
  if (!key || typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
}

function writeSet(companyId, set) {
  const key = KEY(companyId);
  if (!key || typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify([...set]));
}

/** Synchronous, cache-backed read (used by list filters). */
export function loadResolvedReviewIds(companyId = getCurrentUser()?.company?.id) {
  return readSet(companyId);
}

/** Fetch the authoritative resolved-id set from the server and refresh cache. */
export async function syncResolvedReviewIds(companyId = getCurrentUser()?.company?.id) {
  try {
    const res = await api.get("/api/reviews/resolved/", { auth: true });
    const set = new Set(res?.ids || []);
    writeSet(companyId, set);
    return set;
  } catch {
    return readSet(companyId);
  }
}

/** Optimistically mark resolved (cache) and persist to the server. */
export function markReviewResolved(reviewId, companyId = getCurrentUser()?.company?.id) {
  if (reviewId == null) return;
  const set = readSet(companyId);
  set.add(reviewId);
  writeSet(companyId, set);
  api.post(`/api/reviews/${reviewId}/resolve/`, {}, { auth: true }).catch(() => {});
}

export function unmarkReviewResolved(reviewId, companyId = getCurrentUser()?.company?.id) {
  if (reviewId == null) return;
  const set = readSet(companyId);
  set.delete(reviewId);
  writeSet(companyId, set);
  api.delete(`/api/reviews/${reviewId}/resolve/`, { auth: true }).catch(() => {});
}

export function isReviewResolved(reviewId, companyId = getCurrentUser()?.company?.id) {
  return readSet(companyId).has(reviewId);
}
